import React, { useMemo, useState, useEffect } from 'react';
import { Button, Space, message, Popconfirm, Input, Upload, Tabs, Form, DatePicker, InputNumber, Checkbox, Radio, Select, Modal, Table } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FormOutlined, ReloadOutlined, UploadOutlined, InboxOutlined, SyncOutlined, CloudUploadOutlined, RollbackOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ModalForm, ProFormText, ProFormDateTimePicker, ProFormDigit, ProFormSelect, ProFormDatePicker } from '@ant-design/pro-components';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import { renderDateTime, ActionButtons, ResizableTable } from './utils';
import ReactQuill from 'react-quill';
import { normalizeQuillHtml } from '../../utils/richText';
import { quillClipboardForceParagraphMatchers } from '../../utils/quillClipboardFix';

interface Package {
  id?: number;
  package_name: string;
  package_content: string;
  package_url: string;
}

interface Announcement {
  id: number;
  project_no: string;
  project_name: string;
  announcement_url: string;
  bidding_unit: string;
  project_nature: string;
  billing_method: string;
  procurement_method: string;
  document_price: number;
  file_get_start_time: string;
  file_get_end_time: string;
  open_bid_time: string;
  budget: number;
  qualification: string;
  packages_num: number;
  project_managers: string[];
  status_flag: number;
  create_time: string;
  packages?: Package[];
  allow_e_bid?: number;
  allow_joint_bid?: number;
  notice_type?: number;
  notice_content?: string;
  notice_url?: string;
  publish_time?: string;
  is_hide?: number;
}

interface ProjectManager {
  member_name: string;
}

interface Purchaser {
  id: number;
  company_name: string;
}

const projectNatureOptions = ['政府采购', '非政府采购', '企业招标', '国际招标'];
const billingMethodOptions = ['按项目收费', '按包收费'];
const procurementMethodOptions = ['公开招标', '竞争性磋商', '询价', '单一来源', '竞争性谈判', '比选', '竞争性评比', '其他'];

export default function BidTender() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Announcement | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();
  const [projectManagers, setProjectManagers] = useState<ProjectManager[]>([]);
  const [purchasers, setPurchasers] = useState<Purchaser[]>([]);
  const [packages, setPackages] = useState<Package[]>([{ package_name: '第1包', package_content: '', package_url: '' }]);
  const [activePackageTab, setActivePackageTab] = useState('0');
  const [uploading, setUploading] = useState(false);
  const [sharedPdfUrl, setSharedPdfUrl] = useState('');
  const [noticeType, setNoticeType] = useState<number>(0);
  const [noticeContent, setNoticeContent] = useState('');
  const [noticeUrl, setNoticeUrl] = useState('');
  const [noticeUploading, setNoticeUploading] = useState(false);
  const [allowEBid, setAllowEBid] = useState<number>(0);
  const [allowJointBid, setAllowJointBid] = useState<number>(0);
  const [procurementMethod, setProcurementMethod] = useState<string>('');
  const [customProcurementMethod, setCustomProcurementMethod] = useState<string>('');

  // 富文本编辑器（仅文字编辑工具栏：去掉图片/附件/公式等）
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['clean'],
      ],
      clipboard: {
        matchVisual: false,
        matchers: quillClipboardForceParagraphMatchers,
      },
    }),
    [],
  );

  // 变更公告相关状态
  const [changeModalVisible, setChangeModalVisible] = useState(false);
  const [changeListModalVisible, setChangeListModalVisible] = useState(false);
  const [changeListLoading, setChangeListLoading] = useState(false);
  const [changeList, setChangeList] = useState<Array<{ id: number; project_name: string; publish_time: string; pubulish_version: number }>>([]);
  const [editingChangeId, setEditingChangeId] = useState<number | null>(null);
  const [changeNoticeType, setChangeNoticeType] = useState<number>(0);
  const [changeNoticeContent, setChangeNoticeContent] = useState('');
  const [changeNoticeUrl, setChangeNoticeUrl] = useState('');
  const [changeNoticeUploading, setChangeNoticeUploading] = useState(false);
  const [changeFileUrl, setChangeFileUrl] = useState('');
  const [changeFileUploading, setChangeFileUploading] = useState(false);
  const [currentRecordForChange, setCurrentRecordForChange] = useState<Announcement | null>(null);
  const [changeProjectName, setChangeProjectName] = useState('');
  const [changeAnnouncementUrl, setChangeAnnouncementUrl] = useState('');
  const [changeOpenBidTime, setChangeOpenBidTime] = useState<string>('');
  const [changePublishTime, setChangePublishTime] = useState<string>(dayjs().format('YYYY-MM-DD'));

  // 检查是否是项目经理
  const adminUser = localStorage.getItem('admin_user');
  const userInfo = adminUser ? JSON.parse(adminUser) : null;
  const isProjectManager = userInfo?.role_id === 4;

  useEffect(() => {
    fetchAnnouncements();
    fetchProjectManagers();
    fetchPurchasers();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/trade-announcements?type=1', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setAnnouncements(resp.data || []);
      } else {
        message.error(resp?.error || '获取招标公告列表失败');
      }
    } catch (err) {
      message.error('获取招标公告列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectManagers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/members/project-managers', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setProjectManagers(resp.data || []);
      }
    } catch (err) {
      console.error('获取项目经理列表失败:', err);
    }
  };

  const fetchPurchasers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/purchasers', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setPurchasers(resp.data || []);
      }
    } catch (err) {
      console.error('获取采购人列表失败:', err);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldValue('publish_time', dayjs());
    setPackages([{ package_name: '第1包', package_content: '', package_url: '' }]);
    setActivePackageTab('0');
    setSharedPdfUrl('');
    setNoticeType(0);
    setNoticeContent('');
    setNoticeUrl('');
    setAllowEBid(0);
    setAllowJointBid(0);
    setProcurementMethod('');
    setCustomProcurementMethod('');
    setModalVisible(true);
  };

  const handleEdit = async (record: Announcement) => {
    setEditingItem(record);
    
    // 获取公告详情（包含分包信息）
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-announcements/${record.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        const data = resp.data;
        // 解析 project_managers
        let parsedProjectManagers = [];
        if (data.project_managers) {
          try {
            parsedProjectManagers = typeof data.project_managers === 'string' 
              ? JSON.parse(data.project_managers) 
              : data.project_managers;
          } catch (e) {
            parsedProjectManagers = [];
          }
        }

        // 处理采购方式：如果不在选项中，说明是自定义的
        const isCustomProcurement = data.procurement_method && !procurementMethodOptions.includes(data.procurement_method);
        const procurementMethodValue = isCustomProcurement ? '其他' : data.procurement_method;

        form.setFieldsValue({
          project_no: data.project_no,
          project_name: data.project_name,
          announcement_url: data.announcement_url,
          bidding_unit: data.bidding_unit,
          project_nature: data.project_nature,
          billing_method: data.billing_method,
          procurement_method: procurementMethodValue,
          document_price: data.document_price,
          file_get_start_time: data.file_get_start_time ? dayjs(data.file_get_start_time) : undefined,
          file_get_end_time: data.file_get_end_time ? dayjs(data.file_get_end_time) : undefined,
          open_bid_time: data.open_bid_time ? dayjs(data.open_bid_time) : undefined,
          publish_time: data.publish_time ? dayjs(data.publish_time) : dayjs(),
          packages_num: data.packages_num || 1,
          project_managers: parsedProjectManagers,
        });

        setAllowEBid(data.allow_e_bid !== undefined ? data.allow_e_bid : 0);
        setAllowJointBid(data.allow_joint_bid !== undefined ? data.allow_joint_bid : 0);
        setProcurementMethod(procurementMethodValue || '');
        setCustomProcurementMethod(isCustomProcurement ? data.procurement_method : '');
        setNoticeType(data.notice_type !== undefined ? data.notice_type : 0);
        setNoticeContent(normalizeQuillHtml(data.notice_content));
        setNoticeUrl(data.notice_url || '');

        if (data.packages && data.packages.length > 0) {
          setPackages(data.packages);
          // 设置共享附件URL（取第一个分包的URL）
          setSharedPdfUrl(data.packages[0].package_url || '');
        } else {
          setPackages([{ package_name: '第1包', package_content: '', package_url: '' }]);
          setSharedPdfUrl('');
        }
        setActivePackageTab('0');
        setModalVisible(true);
      }
    } catch (err) {
      message.error('获取公告详情失败');
    }
  };

  const handleView = async (record: Announcement) => {
    // 查看模式，打开只读弹窗
    setEditingItem(record);
    
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-announcements/${record.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        const data = resp.data;
        // 解析 project_managers
        let parsedProjectManagers = [];
        if (data.project_managers) {
          try {
            parsedProjectManagers = typeof data.project_managers === 'string' 
              ? JSON.parse(data.project_managers) 
              : data.project_managers;
          } catch (e) {
            parsedProjectManagers = [];
          }
        }

        // 处理采购方式：如果不在选项中，说明是自定义的
        const isCustomProcurement = data.procurement_method && !procurementMethodOptions.includes(data.procurement_method);
        const procurementMethodValue = isCustomProcurement ? '其他' : data.procurement_method;

        form.setFieldsValue({
          project_no: data.project_no,
          project_name: data.project_name,
          announcement_url: data.announcement_url,
          bidding_unit: data.bidding_unit,
          project_nature: data.project_nature,
          billing_method: data.billing_method,
          procurement_method: procurementMethodValue,
          document_price: data.document_price,
          file_get_start_time: data.file_get_start_time ? dayjs(data.file_get_start_time) : undefined,
          file_get_end_time: data.file_get_end_time ? dayjs(data.file_get_end_time) : undefined,
          open_bid_time: data.open_bid_time ? dayjs(data.open_bid_time) : undefined,
          publish_time: data.publish_time ? dayjs(data.publish_time) : dayjs(),
          packages_num: data.packages_num || 1,
          project_managers: parsedProjectManagers,
        });

        setAllowEBid(data.allow_e_bid !== undefined ? data.allow_e_bid : 0);
        setAllowJointBid(data.allow_joint_bid !== undefined ? data.allow_joint_bid : 0);
        setProcurementMethod(procurementMethodValue || '');
        setCustomProcurementMethod(isCustomProcurement ? data.procurement_method : '');
        setNoticeType(data.notice_type !== undefined ? data.notice_type : 0);
        setNoticeContent(normalizeQuillHtml(data.notice_content));
        setNoticeUrl(data.notice_url || '');

        if (data.packages && data.packages.length > 0) {
          setPackages(data.packages);
          setSharedPdfUrl(data.packages[0].package_url || '');
        } else {
          setPackages([{ package_name: '第1包', package_content: '', package_url: '' }]);
          setSharedPdfUrl('');
        }
        setActivePackageTab('0');
        setModalVisible(true);
      }
    } catch (err) {
      message.error('获取公告详情失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchAnnouncements();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleChange = async (record: Announcement) => {
    setCurrentRecordForChange(record);
    setChangeListModalVisible(true);
    await fetchChangeList(record.project_no);
  };

  const fetchChangeList = async (projectNo: string) => {
    setChangeListLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-announcements/change-list/${encodeURIComponent(projectNo)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setChangeList(resp.data || []);
      } else {
        message.error(resp?.error || '获取变更公告列表失败');
      }
    } catch (err) {
      message.error('获取变更公告列表失败');
    } finally {
      setChangeListLoading(false);
    }
  };

  const resetChangeForm = () => {
    setEditingChangeId(null);
    setChangeNoticeType(0);
    setChangeNoticeContent('');
    setChangeNoticeUrl('');
    setChangeFileUrl('');
    setChangeProjectName('');
    setChangeAnnouncementUrl('');
    setChangeOpenBidTime('');
    setChangePublishTime(dayjs().format('YYYY-MM-DD'));
  };

  const openCreateChangeModal = () => {
    if (!currentRecordForChange) return;
    resetChangeForm();
    setChangeProjectName(`${currentRecordForChange.project_name}-变更公告`);
    setChangeAnnouncementUrl(currentRecordForChange.announcement_url);
    setChangeOpenBidTime(currentRecordForChange.open_bid_time || '');
    setChangePublishTime(dayjs().format('YYYY-MM-DD'));
    setChangeModalVisible(true);
  };

  const openEditChangeModal = async (changeId: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-announcements/${changeId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (!(resp && resp.ok && resp.data)) {
        message.error(resp?.error || '获取变更公告详情失败');
        return;
      }

      const data = resp.data;
      setEditingChangeId(changeId);
      setChangeProjectName(data.project_name || '');
      setChangeAnnouncementUrl(data.announcement_url || '');
      // 后端可能返回 Date 对象或字符串，这里统一格式化为字符串，避免提交时写入非法日期导致 500
      const openBid = data.open_bid_time ? dayjs(data.open_bid_time) : null;
      setChangeOpenBidTime(openBid && openBid.isValid() ? openBid.format('YYYY-MM-DD HH:mm:ss') : '');

      const publish = data.publish_time ? dayjs(data.publish_time) : null;
      setChangePublishTime(publish && publish.isValid() ? publish.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'));
      setChangeNoticeType(data.notice_type !== undefined ? data.notice_type : 0);
      setChangeNoticeContent(normalizeQuillHtml(data.notice_content));
      setChangeNoticeUrl(data.notice_url || '');
      setChangeFileUrl(data.content_pdf || '');
      setChangeModalVisible(true);
    } catch (err) {
      message.error('获取变更公告详情失败');
    }
  };

  const handleHardDeleteChange = async (changeId: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-announcements/${changeId}/hard-delete`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        if (currentRecordForChange?.project_no) {
          await fetchChangeList(currentRecordForChange.project_no);
        }
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleTogglePublish = async (record: Announcement, targetIsHide: 0 | 1) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-announcements/${record.id}/is-hide`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ is_hide: targetIsHide }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(targetIsHide === 0 ? '发布成功' : '退回成功');
        setAnnouncements(prev => prev.map(a => (a.id === record.id ? { ...a, is_hide: targetIsHide } : a)));
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  const handleNoticeFileUpload = async (file: File) => {
    setNoticeUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const resp = await response.json();

      if (resp && resp.ok) {
        setNoticeUrl(resp.url);
        message.success('上传成功');
      } else {
        message.error(resp?.error || '上传失败');
      }
    } catch (err: any) {
      console.error('上传错误:', err);
      message.error('上传失败: ' + (err?.message || '网络错误'));
    } finally {
      setNoticeUploading(false);
    }

    return false;
  };

  const handleChangeNoticeFileUpload = async (file: File) => {
    setChangeNoticeUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const resp = await response.json();

      if (resp && resp.ok) {
        setChangeNoticeUrl(resp.url);
        message.success('上传成功');
      } else {
        message.error(resp?.error || '上传失败');
      }
    } catch (err: any) {
      console.error('上传错误:', err);
      message.error('上传失败: ' + (err?.message || '网络错误'));
    } finally {
      setChangeNoticeUploading(false);
    }

    return false;
  };

  const handleChangeFileUpload = async (file: File) => {
    setChangeFileUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const resp = await response.json();

      if (resp && resp.ok) {
        setChangeFileUrl(resp.url);
        message.success('上传成功');
      } else {
        message.error(resp?.error || '上传失败');
      }
    } catch (err: any) {
      console.error('上传错误:', err);
      message.error('上传失败: ' + (err?.message || '网络错误'));
    } finally {
      setChangeFileUploading(false);
    }

    return false;
  };

  const handleChangeSubmit = async () => {
    if (!currentRecordForChange) {
      return;
    }

    try {
      // 验证所有必填字段
      if (!changeProjectName || !changeProjectName.trim()) {
        message.error('请输入公告名称');
        return;
      }
      if (!changeAnnouncementUrl || !changeAnnouncementUrl.trim()) {
        message.error('请输入公告链接');
        return;
      }
      if (!changeOpenBidTime || !changeOpenBidTime.trim()) {
        message.error('请选择开标时间');
        return;
      }
      if (!changePublishTime || !changePublishTime.trim()) {
        message.error('请选择发布时间');
        return;
      }
      
      // 验证变更公告填写
      if (changeNoticeType === 0 && (!changeNoticeContent.trim() || changeNoticeContent === '<p><br></p>')) {
        message.error('请输入变更公告内容');
        return;
      }
      if (changeNoticeType === 1 && !changeNoticeUrl) {
        message.error('请上传变更公告文件');
        return;
      }
      
      // 验证变更招标文件
      if (!changeFileUrl || !changeFileUrl.trim()) {
        message.error('请上传变更招标文件');
        return;
      }

      const token = localStorage.getItem('admin_token');

      // 创建/编辑变更公告
      const submitData = {
        project_no: currentRecordForChange.project_no,
        project_name: changeProjectName,
        announcement_url: changeAnnouncementUrl,
        open_bid_time: changeOpenBidTime || null,
        publish_time: changePublishTime || null,
        content_pdf: changeFileUrl || null,
        notice_type: changeNoticeType,
        notice_content: changeNoticeType === 0 ? changeNoticeContent : null,
        notice_url: changeNoticeType === 1 ? changeNoticeUrl : null,
      };

      const url = editingChangeId
        ? `/api/admin/trade-announcements/${editingChangeId}`
        : '/api/admin/trade-announcements';
      const method = editingChangeId ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(editingChangeId ? submitData : { ...submitData, announcement_type: 2 }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        // 如果上传了变更招标文件，更新对应的 trade_announcement_package
        if (changeFileUrl && currentRecordForChange.id) {
          try {
            await fetch(`/api/admin/trade-announcements/${currentRecordForChange.id}/update-packages`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ package_url: changeFileUrl }),
            });
          } catch (err) {
            console.error('更新分包文件失败:', err);
            message.warning('变更公告保存成功，但更新分包文件失败');
          }
        }

        message.success(editingChangeId ? '变更公告更新成功' : '变更公告创建成功');
        setChangeModalVisible(false);
        resetChangeForm();
        if (currentRecordForChange?.project_no) {
          await fetchChangeList(currentRecordForChange.project_no);
        }
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err: any) {
      if (err.errorFields) {
        message.error('请填写所有必填项');
      } else {
        message.error('操作失败');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // 验证分包信息
      const validPackages = packages.filter(p => p.package_name && p.package_content);
      if (validPackages.length === 0) {
        message.error('请至少填写一个完整的分包信息');
        return;
      }

      // 验证是否上传了共享附件
      if (!sharedPdfUrl) {
        message.error('请上传招标文件');
        return;
      }

      // 验证招标公告填写
      if (noticeType === 0 && (!noticeContent.trim() || noticeContent === '<p><br></p>')) {
        message.error('请输入招标公告内容');
        return;
      }
      if (noticeType === 1 && !noticeUrl) {
        message.error('请上传招标公告文件');
        return;
      }

      // 验证采购方式：如果选择了"其他"，自定义输入框不能为空
      if (procurementMethod === '其他' && (!customProcurementMethod || !customProcurementMethod.trim())) {
        message.error('请输入采购方式');
        return;
      }

      // 为所有分包设置相同的附件URL
      const packagesWithUrl = validPackages.map(p => ({
        ...p,
        package_url: sharedPdfUrl
      }));

      const token = localStorage.getItem('admin_token');
      const url = editingItem
        ? `/api/admin/trade-announcements/${editingItem.id}`
        : '/api/admin/trade-announcements';
      const method = editingItem ? 'PUT' : 'POST';

      // 处理采购方式：如果选择了"其他"，使用自定义值
      const finalProcurementMethod = procurementMethod === '其他' ? customProcurementMethod : values.procurement_method;

      const submitData = {
        ...values,
        announcement_type: 1, // 招标公告
        // 新增招标公告默认不发布，需手动发布（is_hide=1）
        ...(editingItem ? {} : { is_hide: 1 }),
        procurement_method: finalProcurementMethod,
        file_get_start_time: values.file_get_start_time?.format?.('YYYY-MM-DD HH:mm:ss'),
        file_get_end_time: values.file_get_end_time?.format?.('YYYY-MM-DD HH:mm:ss'),
        open_bid_time: values.open_bid_time?.format?.('YYYY-MM-DD HH:mm:ss'),
        publish_time: values.publish_time?.format?.('YYYY-MM-DD'),
        project_managers: values.project_managers || [], // 确保 project_managers 是数组
        packages: packagesWithUrl,
        allow_e_bid: allowEBid,
        allow_joint_bid: allowJointBid,
        notice_type: noticeType,
        notice_content: noticeType === 0 ? noticeContent : null,
        notice_url: noticeType === 1 ? noticeUrl : null,
      };

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('保存成功');
        setModalVisible(false);
        fetchAnnouncements();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err: any) {
      if (err.errorFields) {
        message.error('请填写所有必填项');
      } else {
        message.error('操作失败');
      }
    }
  };

  const handlePackageChange = (index: number, field: keyof Package, value: string) => {
    const newPackages = [...packages];
    newPackages[index] = { ...newPackages[index], [field]: value };
    setPackages(newPackages);
  };

  const handleAddPackage = () => {
    if (packages.length >= 5) {
      message.warning('最多只能添加5个分包');
      return;
    }
    const newPackage = { package_name: `第${packages.length + 1}包`, package_content: '', package_url: '' };
    setPackages([...packages, newPackage]);
    setActivePackageTab(String(packages.length));
  };

  const handleRemovePackage = (index: number) => {
    if (packages.length <= 1) {
      message.warning('至少需要保留一个分包');
      return;
    }
    const newPackages = packages.filter((_, i) => i !== index);
    setPackages(newPackages);
    if (parseInt(activePackageTab) >= newPackages.length) {
      setActivePackageTab(String(newPackages.length - 1));
    }
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const resp = await response.json();

      if (resp && resp.ok) {
        setSharedPdfUrl(resp.url);
        message.success('上传成功');
      } else {
        message.error(resp?.error || '上传失败');
      }
    } catch (err: any) {
      console.error('上传错误:', err);
      message.error('上传失败: ' + (err?.message || '网络错误'));
    } finally {
      setUploading(false);
    }

    return false;
  };

  // 过滤数据
  const filteredData = announcements.filter(a => 
    a.project_no?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.project_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.bidding_unit?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const columns: ColumnsType<Announcement> = [
    {
      title: '项目编号',
      dataIndex: 'project_no',
      key: 'project_no',
      width: 150,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '采购人',
      dataIndex: 'bidding_unit',
      key: 'bidding_unit',
      ellipsis: true,
    },
    {
      title: '文件售价',
      dataIndex: 'document_price',
      key: 'document_price',
      width: 100,
      render: (price: number | null | undefined) => (
        <span style={{ color: '#f59e0b', fontWeight: 600 }}>
          ¥{price != null ? Number(price).toFixed(2) : '0.00'}
        </span>
      ),
    },
    {
      title: '开标时间',
      dataIndex: 'open_bid_time',
      key: 'open_bid_time',
      width: 170,
      render: renderDateTime,
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size={8}>
          {!isProjectManager && (
            record.is_hide === 1 ? (
              <Popconfirm title="是否发布此公告？" onConfirm={() => handleTogglePublish(record, 0)}>
                <Button
                  size="small"
                  className="admin-action-btn publish-btn"
                  icon={<CloudUploadOutlined />}
                >
                  发布
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm title="是否退回此公告？" onConfirm={() => handleTogglePublish(record, 1)}>
                <Button
                  size="small"
                  className="admin-action-btn withdraw-btn"
                  icon={<RollbackOutlined />}
                >
                  退回
                </Button>
              </Popconfirm>
            )
          )}
          {!isProjectManager && (
            <Button
              size="small"
              className="admin-action-btn change-btn"
              icon={<SyncOutlined />}
              onClick={() => handleChange(record)}
              style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: 'white' }}
            >
              变更
            </Button>
          )}
          <ActionButtons
            onEdit={() => handleEdit(record)}
            onDelete={() => handleDelete(record.id)}
            onView={() => handleView(record)}
            deleteTitle="确定要删除这条招标公告吗？"
            showEdit={!isProjectManager}
            showDelete={!isProjectManager}
            showView={isProjectManager}
          />
        </Space>
      ),
    },
  ];

  const packageTabItems = packages.map((pkg, index) => ({
    key: String(index),
    label: pkg.package_name || `第${index + 1}包`,
    children: (
      <div style={{ padding: '16px 0' }}>
        <Form.Item label="包件名称" required>
          <Input
            value={pkg.package_name}
            onChange={(e) => handlePackageChange(index, 'package_name', e.target.value)}
            placeholder="请输入包件名称"
          />
        </Form.Item>
        <Form.Item label="包件内容" required>
          <Input.TextArea
            value={pkg.package_content}
            onChange={(e) => handlePackageChange(index, 'package_content', e.target.value)}
            placeholder="请输入包件内容"
            rows={4}
          />
        </Form.Item>
        {packages.length > 1 && (
          <Button danger onClick={() => handleRemovePackage(index)}>
            删除此分包
          </Button>
        )}
      </div>
    ),
  }));

  return (
    <div className="admin-page">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <FormOutlined />
          </div>
          <div>
            <h2>招标公告</h2>
            <p>管理所有招标公告信息</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索项目编号/名称..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchAnnouncements}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          {!isProjectManager && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAdd}
              style={{ 
                borderRadius: 10,
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
            >
              发布招标公告
            </Button>
          )}
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <ResizableTable
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      {/* 编辑弹窗 */}
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FormOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>
              {isProjectManager ? '查看招标公告' : (editingItem ? '修改招标公告' : '发布招标公告')}
            </span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={isProjectManager ? undefined : handleSubmit}
        width={900}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
          wrapClassName: 'admin-richtext-modal',
        }}
        submitter={isProjectManager ? false : {
          searchConfig: { submitText: '保存', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24, maxHeight: '60vh', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormText
              name="project_no"
              label="项目编号"
              placeholder="请输入项目编号"
              rules={[{ required: true, message: '请输入项目编号' }]}
              fieldProps={{ disabled: isProjectManager }}
            />
            <ProFormText
              name="project_name"
              label="项目名称"
              placeholder="请输入项目名称"
              rules={[{ required: true, message: '请输入项目名称' }]}
              fieldProps={{ disabled: isProjectManager }}
            />
          </div>
          
          <ProFormText
            name="announcement_url"
            label="公告链接"
            placeholder="请输入公告链接"
            rules={[{ required: true, message: '请输入公告链接' }]}
            fieldProps={{ disabled: isProjectManager }}
          />
          
          <ProFormSelect
            name="bidding_unit"
            label="采购人"
            placeholder="请选择或输入采购人"
            rules={[{ required: true, message: '请选择采购人' }]}
            fieldProps={{
              disabled: isProjectManager,
              showSearch: true,
              filterOption: (input, option) => {
                const label = option?.label as string || '';
                return label.toLowerCase().includes(input.toLowerCase());
              },
              options: purchasers.map(p => ({
                label: p.company_name,
                value: p.company_name,
              })),
              notFoundContent: purchasers.length === 0 ? '暂无采购人数据' : undefined,
            }}
          />
          
          <Form.Item
            name="project_nature"
            label="项目性质"
            rules={[{ required: true, message: '请选择项目性质' }]}
          >
            <Radio.Group buttonStyle="solid" disabled={isProjectManager}>
              {projectNatureOptions.map(opt => (
                <Radio.Button key={opt} value={opt}>{opt}</Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
          
          <Form.Item
            name="billing_method"
            label="计费方式"
            rules={[{ required: true, message: '请选择计费方式' }]}
          >
            <Radio.Group buttonStyle="solid" disabled={isProjectManager}>
              {billingMethodOptions.map(opt => (
                <Radio.Button key={opt} value={opt}>{opt}</Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
          
          <Form.Item
            name="procurement_method"
            label="采购方式"
            rules={[{ required: true, message: '请选择采购方式' }]}
          >
            <Radio.Group 
              buttonStyle="solid" 
              disabled={isProjectManager}
              value={procurementMethod}
              onChange={(e) => {
                setProcurementMethod(e.target.value);
                if (e.target.value !== '其他') {
                  setCustomProcurementMethod('');
                }
                form.setFieldValue('procurement_method', e.target.value);
              }}
            >
              {procurementMethodOptions.map(opt => (
                <Radio.Button key={opt} value={opt}>{opt}</Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
          {procurementMethod === '其他' && (
            <Form.Item
              label=""
              required
              rules={[{ required: true, message: '请输入采购方式' }]}
            >
              <Input
                placeholder="请输入采购方式"
                value={customProcurementMethod}
                onChange={(e) => setCustomProcurementMethod(e.target.value)}
                disabled={isProjectManager}
                style={{ marginTop: 8 }}
              />
            </Form.Item>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormDigit
              name="document_price"
              label="采购文件售价"
              placeholder="请输入售价"
              min={0}
              fieldProps={{ precision: 2, prefix: '¥', disabled: isProjectManager }}
              rules={[{ required: true, message: '请输入采购文件售价' }]}
            />
            <ProFormDigit
              name="packages_num"
              label="项目包数"
              placeholder="请输入包数"
              min={1}
              max={10}
              fieldProps={{ disabled: isProjectManager }}
              rules={[{ required: true, message: '请输入项目包数' }]}
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormDateTimePicker
              name="file_get_start_time"
              label="获取文件开始时间"
              placeholder="请选择时间"
              rules={[{ required: true, message: '请选择获取文件开始时间' }]}
              fieldProps={{ disabled: isProjectManager, style: { width: '220px' } }}
            />
            <ProFormDateTimePicker
              name="file_get_end_time"
              label="获取文件结束时间"
              placeholder="请选择时间"
              rules={[{ required: true, message: '请选择获取文件结束时间' }]}
              fieldProps={{ disabled: isProjectManager, style: { width: '220px' } }}
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormDateTimePicker
              name="open_bid_time"
              label="开标时间"
              placeholder="请选择开标时间"
              rules={[{ required: true, message: '请选择开标时间' }]}
              fieldProps={{ disabled: isProjectManager, style: { width: '220px' } }}
            />
            <ProFormDatePicker
              name="publish_time"
              label="发布时间"
              placeholder="请选择发布时间"
              rules={[{ required: true, message: '请选择发布时间' }]}
              fieldProps={{ disabled: isProjectManager, style: { width: '220px' } }}
            />
          </div>

          <Form.Item name="project_managers" label="项目经理">
            {projectManagers.length > 0 ? (
              <Checkbox.Group
                style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}
                disabled={isProjectManager}
              >
                {projectManagers.map(pm => (
                  <Checkbox key={pm.member_name} value={pm.member_name}>{pm.member_name}</Checkbox>
                ))}
              </Checkbox.Group>
            ) : (
              <div style={{ color: '#999', fontSize: 14 }}>暂无项目经理数据</div>
            )}
          </Form.Item>

          {/* 投标选项 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item label="是否允许电子投标">
              <Radio.Group
                buttonStyle="solid"
                value={allowEBid}
                onChange={(e) => setAllowEBid(e.target.value)}
                disabled={isProjectManager}
              >
                <Radio.Button value={1}>是</Radio.Button>
                <Radio.Button value={0}>否</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item label="是否允许联合体投标">
              <Radio.Group
                buttonStyle="solid"
                value={allowJointBid}
                onChange={(e) => setAllowJointBid(e.target.value)}
                disabled={isProjectManager}
              >
                <Radio.Button value={1}>是</Radio.Button>
                <Radio.Button value={0}>否</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </div>

          {/* 招标公告填写区域 */}
          <div style={{
            marginTop: 24,
            padding: 16,
            background: '#f9fafb',
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>招标公告填写区域</h4>
            <Form.Item label="公告类型" required>
              <Radio.Group
                buttonStyle="solid"
                value={noticeType}
                onChange={(e) => setNoticeType(e.target.value)}
                disabled={isProjectManager}
              >
                <Radio.Button value={0}>输入文本</Radio.Button>
                <Radio.Button value={1}>上传公告文本</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {noticeType === 0 ? (
              <Form.Item label="公告内容" required>
                <ReactQuill
                  className="admin-richtext-editor"
                  theme="snow"
                  value={noticeContent || ''}
                  onChange={setNoticeContent}
                  modules={quillModules}
                  placeholder="请输入招标公告内容..."
                  style={{ height: 350, marginBottom: 60 }}
                  readOnly={isProjectManager}
                />
              </Form.Item>
            ) : (
              <div style={{ padding: 16, background: '#fff', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                <Form.Item
                  label={
                    <span>
                      上传公告文件
                      <span style={{ color: '#ff4d4f', marginLeft: 8, fontSize: 12 }}>上传PDF文件尽量在50M内</span>
                    </span>
                  }
                  required
                  style={{ marginBottom: 0 }}
                >
                  <Upload.Dragger
                    name="file"
                    accept=".pdf"
                    showUploadList={false}
                    beforeUpload={handleNoticeFileUpload}
                    disabled={noticeUploading || isProjectManager}
                  >
                    {noticeUrl ? (
                      <div style={{ padding: '20px' }}>
                        <p style={{ color: '#52c41a', marginBottom: 8 }}>✓ 已上传公告文本</p>
                        <p style={{ color: '#999', fontSize: 12 }}>{noticeUrl}</p>
                        <p style={{ color: '#3b82f6', marginTop: 8 }}>点击重新上传</p>
                        <br/>
                        <a href={noticeUrl}  target="_blank">预览上传公告文件</a>
                      </div>
                    ) : (
                      <div style={{ padding: '20px' }}>
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined style={{ color: '#3b82f6', fontSize: 48 }} />
                        </p>
                        <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                        <p className="ant-upload-hint">仅支持 PDF 格式文件</p>
                      </div>
                    )}
                  </Upload.Dragger>
                </Form.Item>
              </div>
            )}
          </div>

          {/* 分包信息区域 */}
          <div style={{ 
            marginTop: 24, 
            padding: 16, 
            background: '#f9fafb', 
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h4 style={{ margin: 0, color: '#1f2937' }}>分包信息填报区域</h4>
              {packages.length < 5 && !isProjectManager && (
                <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddPackage}>
                  添加分包
                </Button>
              )}
            </div>
            
            <Tabs
              activeKey={activePackageTab}
              onChange={setActivePackageTab}
              items={packageTabItems}
              type="card"
            />
            
            {/* 共享上传附件区域 */}
            <div style={{ marginTop: 16, padding: 16, background: '#fff', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
              <Form.Item
                label={
                  <span>
                    上传招标文件（所有分包共用）
                    <span style={{ color: '#ff4d4f', marginLeft: 8, fontSize: 12 }}>上传PDF文件尽量在50M内</span>
                  </span>
                }
                required
                style={{ marginBottom: 0 }}
              >
                <Upload.Dragger
                  name="file"
                  accept=".pdf"
                  showUploadList={false}
                  beforeUpload={handleFileUpload}
                  disabled={uploading || isProjectManager}
                >
                  {sharedPdfUrl ? (
                    <div style={{ padding: '20px' }}>
                      <p style={{ color: '#52c41a', marginBottom: 8 }}>✓ 已上传附件</p>
                      <p style={{ color: '#999', fontSize: 12 }}>{sharedPdfUrl}</p>
                      
                        <p style={{ color: '#3b82f6', margin: 0 }}>点击重新上传</p>
                        <p>
                        <a href={sharedPdfUrl}  target="_top">预览上传招标文件</a></p>
                      
                    </div>
                  ) : (
                    <div style={{ padding: '20px' }}>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: '#3b82f6', fontSize: 48 }} />
                      </p>
                      <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                      <p className="ant-upload-hint">仅支持 PDF 格式文件，此附件将应用于所有分包</p>
                    </div>
                  )}
                </Upload.Dragger>
              </Form.Item>
            </div>
          </div>
        </div>
      </ModalForm>

      {/* 变更公告弹窗 */}
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #fa8c16, #d46b08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SyncOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingChangeId ? '编辑变更公告' : '发布变更公告'}</span>
          </div>
        }
        open={changeModalVisible}
        onOpenChange={setChangeModalVisible}
        onFinish={handleChangeSubmit}
        width={800}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
          wrapClassName: 'admin-richtext-modal',
        }}
        submitter={{
          searchConfig: { submitText: '保存', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24, maxHeight: '60vh', overflowY: 'auto' }}>
          <Form.Item label="项目编号">
            <Input value={currentRecordForChange?.project_no} disabled />
          </Form.Item>
          <Form.Item label="公告名称" required>
            <Input 
              value={changeProjectName} 
              onChange={(e) => setChangeProjectName(e.target.value)}
              placeholder="请输入公告名称"
            />
          </Form.Item>
          <Form.Item label="公告链接" required>
            <Input 
              value={changeAnnouncementUrl} 
              onChange={(e) => setChangeAnnouncementUrl(e.target.value)}
              placeholder="请输入公告链接"
            />
          </Form.Item>
          <Form.Item label="开标时间" required>
            <DatePicker
              showTime
              style={{ width: '100%' }}
              value={changeOpenBidTime ? dayjs(changeOpenBidTime) : null}
              onChange={(date) => setChangeOpenBidTime(date ? date.format('YYYY-MM-DD HH:mm:ss') : '')}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="请选择开标时间"
            />
          </Form.Item>
          <Form.Item label="发布时间" required>
            <DatePicker
              style={{ width: '100%' }}
              value={changePublishTime ? dayjs(changePublishTime) : null}
              onChange={(date) => setChangePublishTime(date ? date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'))}
              format="YYYY-MM-DD"
              placeholder="请选择发布时间"
            />
          </Form.Item>

          {/* 变更公告填写区域 */}
          <div style={{
            marginTop: 24,
            padding: 16,
            background: '#f9fafb',
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>变更公告填写区域</h4>
            <Form.Item label="公告类型" required>
              <Radio.Group
                buttonStyle="solid"
                value={changeNoticeType}
                onChange={(e) => setChangeNoticeType(e.target.value)}
              >
                <Radio.Button value={0}>输入文本</Radio.Button>
                <Radio.Button value={1}>上传公告文本</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {changeNoticeType === 0 ? (
              <Form.Item label="公告内容" required>
                <ReactQuill
                  className="admin-richtext-editor"
                  theme="snow"
                  value={changeNoticeContent || ''}
                  onChange={setChangeNoticeContent}
                  modules={quillModules}
                  placeholder="请输入变更公告内容..."
                  style={{ height: 350, marginBottom: 60 }}
                />
              </Form.Item>
            ) : (
              <div style={{ padding: 16, background: '#fff', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                <Form.Item
                  label={
                    <span>
                      上传公告文件
                      <span style={{ color: '#ff4d4f', marginLeft: 8, fontSize: 12 }}>上传PDF文件尽量在50M内</span>
                    </span>
                  }
                  required
                  style={{ marginBottom: 0 }}
                >
                  <Upload.Dragger
                    name="file"
                    accept=".pdf"
                    showUploadList={false}
                    beforeUpload={handleChangeNoticeFileUpload}
                    disabled={changeNoticeUploading}
                  >
                    {changeNoticeUrl ? (
                      <div style={{ padding: '20px' }}>
                        <p style={{ color: '#52c41a', marginBottom: 8 }}>✓ 已上传公告文本</p>
                        <p style={{ color: '#999', fontSize: 12 }}>{changeNoticeUrl}</p>
                        <p style={{ color: '#3b82f6', marginTop: 8 }}>点击重新上传</p>
                      </div>
                    ) : (
                      <div style={{ padding: '20px' }}>
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined style={{ color: '#3b82f6', fontSize: 48 }} />
                        </p>
                        <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                        <p className="ant-upload-hint">仅支持 PDF 格式文件</p>
                      </div>
                    )}
                  </Upload.Dragger>
                </Form.Item>
              </div>
            )}
          </div>

          {/* 变更招标文件上传 */}
          <div style={{ marginTop: 24, padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <Form.Item
              label={
                <span>
                  变更招标文件
                  <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>将更新所有分包的招标文件</span>
                </span>
              }
              required
            >
              <Upload.Dragger
                name="file"
                accept=".pdf"
                showUploadList={false}
                beforeUpload={handleChangeFileUpload}
                disabled={changeFileUploading}
              >
                {changeFileUrl ? (
                  <div style={{ padding: '20px' }}>
                    <p style={{ color: '#52c41a', marginBottom: 8 }}>✓ 已上传变更招标文件</p>
                    <p style={{ color: '#999', fontSize: 12 }}>{changeFileUrl}</p>
                    <p style={{ color: '#3b82f6', marginTop: 8 }}>点击重新上传</p>
                  </div>
                ) : (
                  <div style={{ padding: '20px' }}>
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined style={{ color: '#3b82f6', fontSize: 48 }} />
                    </p>
                    <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                    <p className="ant-upload-hint">仅支持 PDF 格式文件，将更新所有分包的招标文件</p>
                  </div>
                )}
              </Upload.Dragger>
            </Form.Item>
          </div>
        </div>
      </ModalForm>

      {/* 变更公告列表弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>变更公告列表</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateChangeModal} disabled={!currentRecordForChange}>
              新增变更公告
            </Button>
          </div>
        }
        open={changeListModalVisible}
        onCancel={() => {
          setChangeListModalVisible(false);
          setChangeList([]);
          resetChangeForm();
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#6b7280' }}>
          项目编号：<span style={{ fontFamily: 'monospace', color: '#111827' }}>{currentRecordForChange?.project_no || '-'}</span>
        </div>
        <Table
          dataSource={changeList}
          loading={changeListLoading}
          rowKey="id"
          pagination={false}
          columns={[
            {
              title: '公告名称',
              dataIndex: 'project_name',
              key: 'project_name',
              ellipsis: true,
            },
            {
              title: '发布时间',
              dataIndex: 'publish_time',
              key: 'publish_time',
              width: 160,
              render: (t: string) => (t ? String(t).slice(0, 10) : '-'),
            },
            {
              title: '版本号',
              dataIndex: 'pubulish_version',
              key: 'pubulish_version',
              width: 100,
              align: 'center',
            },
            {
              title: '操作',
              key: 'action',
              width: 180,
              align: 'center',
              render: (_: unknown, row: any) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEditChangeModal(row.id)}>
                    编辑
                  </Button>
                  <Popconfirm title="确定要删除该变更公告吗？（硬删除）" onConfirm={() => handleHardDeleteChange(row.id)}>
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}

